import { useEffect, useState } from 'react'

interface TimezoneInfo {
  tz: string // IANA timezone, e.g., America/Los_Angeles
  abbr: string // Abbreviation, e.g., PST or PDT
}

const STORAGE_KEY = 'pikm:user_tz_v1'

const guessAbbr = (date: Date, tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    }).formatToParts(date)
    const tzPart = parts.find(p => p.type === 'timeZoneName')?.value
    return tzPart || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const useUserTimezone = (): TimezoneInfo => {
  const [info, setInfo] = useState<TimezoneInfo>(() => {
    const cached = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as TimezoneInfo
        return parsed
      } catch {}
    }
    // Fallback to browser-reported timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    const abbr = guessAbbr(new Date(), tz)
    return { tz, abbr }
  })

  useEffect(() => {
    let cancelled = false
    // Try free IP-based services; use the first that succeeds
    const load = async () => {
      const tryFetch = async (url: string, parse: (d: any) => string | null) => {
        try {
          const res = await fetch(url, { method: 'GET' })
          if (!res.ok) return null
          const data = await res.json()
          return parse(data)
        } catch {
          return null
        }
      }

      const parsers: Array<[string, (d: any) => string | null]> = [
        ['https://ipapi.co/json/', (d) => (d && typeof d.timezone === 'string' ? d.timezone : null)],
        ['https://ipinfo.io/json?token=', (d) => (d && typeof d.timezone === 'string' ? d.timezone : null)],
        ['https://worldtimeapi.org/api/ip', (d) => (d && typeof d.timezone === 'string' ? d.timezone : null)],
      ]

      for (const [url, parser] of parsers) {
        const tz = await tryFetch(url, parser)
        if (cancelled) return
        if (tz) {
          const abbr = guessAbbr(new Date(), tz)
          const next = { tz, abbr }
          setInfo(next)
          try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {}
          return
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  return info
}

export const formatWithTz = (iso: string, tz: string, includeAbbr?: string): string => {
  const d = new Date(iso)
  const datePart = new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(d)
  const timePart = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d)
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(d)
  const abbr = parts.find(p => p.type === 'timeZoneName')?.value || 'UTC'
  return `${datePart} ${timePart} ${abbr}`
}


